/**
 * TanStack DB write-through helpers — persist collection mutations to Supabase.
 *
 * Offline-aware: when the browser is offline or the Supabase request fails with
 * a network error, the mutation is queued in localStorage and replayed when
 * connectivity returns.
 */

import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";
import type { Database } from "@/types/database";
import {
  enqueueOfflineMutation,
  getOfflineQueue,
  isOnline,
  removeOfflineMutation,
  requestBackgroundSync,
  type MutationOperation,
  type QueuedMutation,
} from "./offline-queue";

type MutationBatch<T> = {
  id: string;
  mutations: Array<{
    key: string;
    modified: T;
    original?: T;
  }>;
};

type TableName = keyof Database["public"]["Tables"];

function asMutationBatch<T extends Record<string, unknown>>(
  transaction: unknown
): MutationBatch<T> {
  return transaction as MutationBatch<T>;
}

function table(name: string) {
  return supabase.from(name as TableName);
}

async function resolvePostgresTxid(fallback: string): Promise<string> {
  const client = supabase as unknown as {
    rpc: (
      fn: string,
      args?: Record<string, unknown>
    ) => Promise<{ data: unknown; error: { message: string } | null }>;
  };
  const { data, error } = await client.rpc("get_current_txid");
  if (error || !data) {
    logger.warn("[mutation-handlers] get_current_txid failed, using batch id", {
      error: error?.message,
    });
    return fallback;
  }
  return String(data);
}

function isNetworkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const status = (error as Error & { status?: number }).status;
  if (typeof status === "number" && status === 0) return true;
  if (typeof error.message !== "string") return false;
  if (/aborted|abort/i.test(error.message)) return false;
  return /fetch|network|failed to fetch|offline|internet|net::|ERR_/i.test(
    error.message
  );
}

function queueBatch<T extends Record<string, unknown>>(
  tableName: string,
  operation: MutationOperation,
  transaction: unknown,
  label: string
): QueuedMutation {
  const batch = asMutationBatch<T>(transaction);
  return {
    id: batch.id,
    tableName,
    operation,
    label,
    mutations: batch.mutations.map((mutation) => ({
      key: String(mutation.key),
      modified: (mutation.modified ?? {}) as Record<string, unknown>,
      original: mutation.original as Record<string, unknown> | undefined,
    })),
    enqueuedAt: new Date().toISOString(),
  };
}

function queuedToTransaction(item: QueuedMutation): unknown {
  return {
    id: item.id,
    mutations: item.mutations.map((mutation) => ({
      key: mutation.key,
      modified: mutation.modified,
      original: mutation.original ?? {},
    })),
  };
}

async function executeInserts<T extends Record<string, unknown>>(
  tableName: string,
  transaction: unknown,
  label: string
): Promise<{ txid: string }> {
  const batch = asMutationBatch<T>(transaction);
  await Promise.all(
    batch.mutations.map(async (mutation) => {
      const response = await table(tableName).insert(mutation.modified as never);
      if (response.error) {
        const err = new Error(response.error.message) as Error & {
          status?: number;
        };
        err.status = response.status;
        logger.error(`[${label}] insert failed`, {
          error: response.error.message,
          id: mutation.key,
          status: response.status,
        });
        throw err;
      }
    })
  );
  const txid = await resolvePostgresTxid(batch.id);
  logger.debug(`[${label}] insert persisted`, {
    txid,
    count: batch.mutations.length,
  });
  return { txid };
}

async function executeUpdates<T extends Record<string, unknown>>(
  tableName: string,
  transaction: unknown,
  label: string
): Promise<{ txid: string }> {
  const batch = asMutationBatch<T>(transaction);
  await Promise.all(
    batch.mutations.map(async (mutation) => {
      const response = await table(tableName)
        .update(mutation.modified as never)
        .eq("id", mutation.key);
      if (response.error) {
        const err = new Error(response.error.message) as Error & {
          status?: number;
        };
        err.status = response.status;
        logger.error(`[${label}] update failed`, {
          error: response.error.message,
          id: mutation.key,
          status: response.status,
        });
        throw err;
      }
    })
  );
  const txid = await resolvePostgresTxid(batch.id);
  logger.debug(`[${label}] update persisted`, {
    txid,
    count: batch.mutations.length,
  });
  return { txid };
}

async function executeDeletes(
  tableName: string,
  transaction: unknown,
  label: string
): Promise<{ txid: string }> {
  const batch = asMutationBatch<Record<string, unknown>>(transaction);
  await Promise.all(
    batch.mutations.map(async (mutation) => {
      const response = await table(tableName).delete().eq("id", mutation.key);
      if (response.error) {
        const err = new Error(response.error.message) as Error & {
          status?: number;
        };
        err.status = response.status;
        logger.error(`[${label}] delete failed`, {
          error: response.error.message,
          id: mutation.key,
          status: response.status,
        });
        throw err;
      }
    })
  );
  const txid = await resolvePostgresTxid(batch.id);
  logger.debug(`[${label}] delete persisted`, {
    txid,
    count: batch.mutations.length,
  });
  return { txid };
}

export async function persistInserts<T extends Record<string, unknown>>(
  tableName: string,
  transaction: unknown,
  label: string
): Promise<{ txid: string }> {
  const batch = asMutationBatch<T>(transaction);
  if (!isOnline()) {
    enqueueOfflineMutation(
      queueBatch<T>(tableName, "insert", transaction, label)
    );
    void requestBackgroundSync();
    return { txid: `queued:${batch.id}` };
  }
  try {
    return await executeInserts<T>(tableName, transaction, label);
  } catch (err) {
    if (isNetworkError(err)) {
      enqueueOfflineMutation(
        queueBatch<T>(tableName, "insert", transaction, label)
      );
      void requestBackgroundSync();
      return { txid: `queued:${batch.id}` };
    }
    throw err;
  }
}

export async function persistUpdates<T extends Record<string, unknown>>(
  tableName: string,
  transaction: unknown,
  label: string
): Promise<{ txid: string }> {
  const batch = asMutationBatch<T>(transaction);
  if (!isOnline()) {
    enqueueOfflineMutation(
      queueBatch<T>(tableName, "update", transaction, label)
    );
    void requestBackgroundSync();
    return { txid: `queued:${batch.id}` };
  }
  try {
    return await executeUpdates<T>(tableName, transaction, label);
  } catch (err) {
    if (isNetworkError(err)) {
      enqueueOfflineMutation(
        queueBatch<T>(tableName, "update", transaction, label)
      );
      void requestBackgroundSync();
      return { txid: `queued:${batch.id}` };
    }
    throw err;
  }
}

export async function persistDeletes(
  tableName: string,
  transaction: unknown,
  label: string
): Promise<{ txid: string }> {
  const batch = asMutationBatch<Record<string, unknown>>(transaction);
  if (!isOnline()) {
    enqueueOfflineMutation(
      queueBatch<Record<string, unknown>>(
        tableName,
        "delete",
        transaction,
        label
      )
    );
    void requestBackgroundSync();
    return { txid: `queued:${batch.id}` };
  }
  try {
    return await executeDeletes(tableName, transaction, label);
  } catch (err) {
    if (isNetworkError(err)) {
      enqueueOfflineMutation(
        queueBatch<Record<string, unknown>>(
          tableName,
          "delete",
          transaction,
          label
        )
      );
      void requestBackgroundSync();
      return { txid: `queued:${batch.id}` };
    }
    throw err;
  }
}

let flushPromise: Promise<number> | null = null;

async function flushOfflineQueueInternal(): Promise<number> {
  if (typeof window === "undefined") return 0;
  if (!isOnline()) return 0;

  const { data: sessionData } = await supabase.auth.getSession();
  if (!sessionData.session) {
    logger.debug("[offline] no active session, skipping offline flush");
    return 0;
  }

  const pending = getOfflineQueue();
  if (pending.length === 0) return 0;

  logger.info("[offline] replaying queued mutations", { count: pending.length });
  let processed = 0;
  for (const item of [...pending]) {
    try {
      if (item.operation === "insert") {
        await executeInserts(
          item.tableName,
          queuedToTransaction(item),
          item.label
        );
      } else if (item.operation === "update") {
        await executeUpdates(
          item.tableName,
          queuedToTransaction(item),
          item.label
        );
      } else {
        await executeDeletes(
          item.tableName,
          queuedToTransaction(item),
          item.label
        );
      }
      removeOfflineMutation(item.id);
      processed++;
      logger.debug(`[offline] replayed ${item.label}`, { id: item.id });
    } catch (err) {
      if (isNetworkError(err)) {
        logger.info("[offline] replay paused — still offline", {
          id: item.id,
        });
        break;
      }
      logger.error(
        "[offline] dropping queued mutation after non-network error",
        {
          id: item.id,
          operation: item.operation,
          tableName: item.tableName,
          error: err instanceof Error ? err.message : err,
        }
      );
      removeOfflineMutation(item.id);
    }
  }
  return processed;
}

export function flushOfflineQueue(): Promise<number> {
  if (flushPromise) return flushPromise;
  flushPromise = flushOfflineQueueInternal().finally(() => {
    flushPromise = null;
  });
  return flushPromise;
}
